import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  captureCompleteSchema,
  type CardCapture,
  type CardSummary,
  type CardTransport,
} from "@libs/buyer-identity-client";
import {
  captureAppearance,
  captureHeight,
  captureHtml,
  captureSubmitMessage,
  type CaptureAppearance,
} from "@libs/buyer-identity-client/capture-html";
import {
  CAPTURE_EXPIRED_TEXT,
  captureLease,
  captureRenewalDue,
  isCaptureExpired,
  renewCapture,
  type CaptureLease,
} from "@libs/buyer-identity-client/capture-renewal";

export type CardFieldsHandle = { submit(): Promise<CardSummary> };

/** What the secure fields could not do, and the way to load them afresh. */
export type CardFieldsError = { message: string; reload(): void };

const CONFIRM_FAILED = "Could not confirm your card. Please try again.";
const TIMED_OUT = "Your card could not be confirmed. Please try again.";

/** One press of the host's action, until its card is confirmed or refused. */
type Submission = {
  resolve(card: CardSummary): void;
  reject(error: Error): void;
  timer?: ReturnType<typeof setTimeout>;
  /** The capture the document was asked to make aliases under. */
  target: CardCapture | null;
  /** This press already began a fresh capture: it begins no other. */
  renewed: boolean;
};

/** Card entry belongs to checkout. The host's primary action tokenises these
 * fields; PAN and CVC never pass through the host or its application server. */
export const InlineCardFields = forwardRef<
  CardFieldsHandle,
  {
    transport: CardTransport;
    cardId?: string;
    className?: string;
    /** The host checkout's tokens for the secure fields (and their shape
     *  while they load); the one-line dark group without. */
    appearance?: Partial<CaptureAppearance>;
    onValidityChange?: (valid: boolean) => void;
    onReadyChange?: (ready: boolean) => void;
    onFieldState?: (state: {
      valid: boolean;
      empty: boolean;
      focused: boolean;
    }) => void;
    /** The host says what went wrong, in its own notice and place (and
     *  offers `reload`); without it the fields say it under themselves. */
    onError?: (error: CardFieldsError | null) => void;
  }
>(function InlineCardFields(
  {
    transport,
    cardId,
    className,
    appearance,
    onValidityChange,
    onFieldState,
    onReadyChange,
    onError,
  },
  ref,
) {
  const [capture, setCapture] = useState<CardCapture | null>(null);
  const [error, setError] = useState("");
  const [revision, setRevision] = useState(0);
  const [ready, setReady] = useState(false);
  const [valid, setValid] = useState(false);
  const frame = useRef<HTMLIFrameElement>(null);
  const processing = useRef(false);
  const fieldStateCallback = useRef(onFieldState);
  fieldStateCallback.current = onFieldState;
  const errorCallback = useRef(onError);
  errorCallback.current = onError;
  const hostSaysErrors = !!onError;
  const reload = useCallback(() => setRevision((value) => value + 1), []);
  const submission = useRef<Submission | null>(null);
  // The capture a submit goes to: the drawn one, until a submit renews it.
  const lease = useRef<CaptureLease | null>(null);
  // Read by value: a host passing a fresh object each render must not
  // reload the secure fields.
  const look = JSON.stringify(captureAppearance(appearance));
  const html = useMemo(
    () =>
      capture
        ? captureHtml(capture, {
            inline: true,
            appearance: JSON.parse(look) as Partial<CaptureAppearance>,
            scriptNonce:
              typeof document === "undefined"
                ? undefined
                : document.querySelector<HTMLScriptElement>("script[nonce]")
                    ?.nonce,
          })
        : "",
    [capture, look],
  );
  const height = captureHeight(appearance, !!cardId);
  const [frameUrl, setFrameUrl] = useState("");

  useEffect(() => {
    let active = true;
    lease.current = null;
    setCapture(null);
    setReady(false);
    setValid(false);
    setError("");
    void transport
      .beginCardCapture(cardId)
      .then((value) => {
        if (!active) return;
        lease.current = captureLease(value);
        setCapture(value);
      })
      .catch(() => {
        if (active)
          setError("Could not load the card fields. Please try again.");
      });
    return () => {
      active = false;
    };
  }, [transport, cardId, revision]);

  useEffect(() => {
    onReadyChange?.(ready && !error);
    return () => onReadyChange?.(false);
  }, [ready, error, onReadyChange]);

  useEffect(() => {
    if (!capture || ready || error) return;
    const timer = setTimeout(
      () => setError("Could not load the card fields. Please try again."),
      15_000,
    );
    return () => clearTimeout(timer);
  }, [capture, ready, error]);

  useEffect(() => {
    errorCallback.current?.(error ? { message: error, reload } : null);
  }, [error, reload]);
  useEffect(() => () => errorCallback.current?.(null), []);

  useEffect(() => {
    onValidityChange?.(ready && valid && !error);
    return () => onValidityChange?.(false);
  }, [ready, valid, error, onValidityChange]);

  useEffect(() => {
    setReady(false);
    setValid(false);
    if (!html) {
      setFrameUrl("");
      return;
    }
    // A blob preserves the host origin required by VGS frame messaging.
    const url = URL.createObjectURL(new Blob([html], { type: "text/html" }));
    setFrameUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [html]);

  useEffect(
    () => () => {
      if (submission.current) {
        clearTimeout(submission.current.timer);
        submission.current.reject(Error("Card entry closed."));
        submission.current = null;
      }
    },
    [],
  );

  const finish = useCallback((pending: Submission) => {
    clearTimeout(pending.timer);
    if (submission.current === pending) submission.current = null;
    processing.current = false;
  }, []);

  /** Asks the document to make this press's aliases: under the current
   *  capture while it has time left, otherwise (or with `renew`, once the
   *  server said it ended) under a fresh one begun now. What the buyer
   *  typed stays in the fields either way. */
  const send = useCallback(
    async (pending: Submission, renew: boolean) => {
      if (!capture) return;
      clearTimeout(pending.timer);
      pending.timer = setTimeout(() => {
        if (submission.current !== pending) return;
        finish(pending);
        setError(TIMED_OUT);
        pending.reject(Error(TIMED_OUT));
      }, 30000);
      let current = lease.current ?? captureLease(capture);
      if (renew || captureRenewalDue(current)) {
        try {
          current = await renewCapture(transport, cardId, capture);
        } catch {
          if (submission.current !== pending) return;
          finish(pending);
          setError(CONFIRM_FAILED);
          pending.reject(Error(CONFIRM_FAILED));
          return;
        }
        if (submission.current !== pending) return;
        lease.current = current;
        pending.renewed = true;
      }
      pending.target = current.capture;
      frame.current?.contentWindow?.postMessage(
        captureSubmitMessage(capture, current.capture),
        window.location.origin,
      );
    },
    [capture, transport, cardId, finish],
  );

  useImperativeHandle(
    ref,
    () => ({
      submit() {
        if (!capture || !ready || !frame.current?.contentWindow)
          return Promise.reject(
            Error(
              "Your card fields are still loading. Please try again in a moment.",
            ),
          );
        if (submission.current)
          return Promise.reject(Error("Your card is being processed."));
        setError("");
        return new Promise<CardSummary>((resolve, reject) => {
          const pending: Submission = {
            resolve,
            reject,
            target: null,
            renewed: false,
          };
          submission.current = pending;
          void send(pending, false);
        });
      },
    }),
    [capture, ready, send],
  );

  useEffect(() => {
    if (!capture) return;
    const listener = (event: MessageEvent) => {
      if (
        event.source !== frame.current?.contentWindow ||
        event.origin !== window.location.origin ||
        typeof event.data !== "string"
      )
        return;
      let message;
      try {
        message = JSON.parse(event.data);
      } catch {
        return;
      }
      if (
        message.type !== "lissen-card-capture" ||
        message.captureId !== capture.id
      )
        return;
      if (message.ready) {
        setReady(true);
        setValid(message.valid === true);
        return;
      }
      if (typeof message.valid === "boolean") {
        fieldStateCallback.current?.({
          valid: message.valid,
          empty: message.empty !== false,
          focused: message.focused === true,
        });
        setValid(message.valid);
        setError("");
        return;
      }
      if (message.error) {
        setValid(false);
        const text =
          message.field === "panAlias"
            ? "Check your card number."
            : message.field === "expiry"
              ? "Check your expiry date."
              : message.field === "cvcAlias"
                ? "Check your security code."
                : "Check your card details and try again.";
        setError(text);
        const pending = submission.current;
        if (pending) {
          finish(pending);
          pending.reject(Error(text));
        }
        return;
      }
      const parsed = captureCompleteSchema.safeParse(message.value);
      const pending = submission.current;
      // Only aliases made under the capture this press asked for.
      if (
        !parsed.success ||
        !pending?.target ||
        parsed.data.captureId !== pending.target.id ||
        processing.current
      )
        return;
      processing.current = true;
      transport.completeCardCapture(parsed.data).then(
        (card) => {
          if (submission.current !== pending) return;
          finish(pending);
          pending.resolve(card);
        },
        (failure: unknown) => {
          if (submission.current !== pending) return;
          const expired = isCaptureExpired(failure);
          // The capture ended while the buyer typed: the aliases are made
          // again, under a fresh capture, from the fields as they are.
          if (expired && !pending.renewed) {
            processing.current = false;
            pending.target = null;
            void send(pending, true);
            return;
          }
          finish(pending);
          const text = expired ? CAPTURE_EXPIRED_TEXT : CONFIRM_FAILED;
          setError(text);
          pending.reject(Error(text));
        },
      );
    };
    window.addEventListener("message", listener);
    return () => window.removeEventListener("message", listener);
  }, [capture, transport, send, finish]);

  return (
    <div className={className}>
      <div
        aria-busy={!ready && !error}
        style={{ position: "relative", height, minWidth: 0 }}
      >
        {/* Until the secure fields are shown, and in their place if they
            could not load: the boxes keep their size either way. */}
        {!ready && <FieldsShape appearance={appearance} code={!!cardId} />}
        {frameUrl && (
          <iframe
            key={frameUrl}
            ref={frame}
            title={cardId ? "Card security code" : "Card information"}
            src={frameUrl}
            sandbox="allow-scripts allow-forms allow-same-origin"
            style={{
              display: "block",
              width: "100%",
              height,
              border: 0,
              background: "transparent",
              colorScheme: "normal",
              visibility: ready ? "visible" : "hidden",
            }}
          />
        )}
      </div>
      {error && !hostSaysErrors && (
        <p role="alert" style={{ fontSize: 13 }}>
          {error}{" "}
          <button type="button" onClick={reload}>
            Reload card fields
          </button>
        </p>
      )}
    </div>
  );
});

/** The secure fields' shape while they load: the boxes they will draw, at
 *  their size, so nothing moves when they arrive. */
function FieldsShape({
  appearance,
  code,
}: {
  appearance?: Partial<CaptureAppearance>;
  code: boolean;
}) {
  const a = captureAppearance(appearance);
  const box = (label: string | null) => (
    <div
      style={{
        border: `1px solid ${a.border}`,
        borderRadius: a.radius,
        background: a.background,
        display: "flex",
        alignItems: "center",
        boxSizing: "border-box",
        height: a.height,
        minWidth: 0,
        padding: "0 12px",
        color: a.placeholder,
        fontSize: a.fontSize,
        fontFamily: a.fontFamily,
      }}
    >
      {label}
    </div>
  );
  return (
    <div
      aria-hidden="true"
      style={{
        position: "absolute",
        inset: 0,
        display: "grid",
        gap: a.gap,
        alignContent: "start",
        pointerEvents: "none",
      }}
    >
      {code ? (
        box("CVC")
      ) : a.layout === "split" ? (
        <>
          {box("Card number")}
          <div
            style={{
              display: "grid",
              gap: a.gap,
              gridTemplateColumns: "1fr 1fr",
            }}
          >
            {box("MM / YY")}
            {box("CVC")}
          </div>
        </>
      ) : (
        box(null)
      )}
    </div>
  );
}
